import { useState, useEffect, useRef, useCallback } from 'react';
import Papa from 'papaparse';
import { Tooltip } from 'react-tooltip';
import Plotly from 'plotly.js-dist';
import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';
import './HealthDataVisualizer.css';

// Format large numbers as abbreviated (2.5B, 500M, 12.3K)
const formatAbbreviated = (value) => {
  const abs = Math.abs(value);
  if (abs >= 1e9) return (value / 1e9).toFixed(1) + 'B';
  if (abs >= 1e6) return (value / 1e6).toFixed(1) + 'M';
  if (abs >= 1e3) return (value / 1e3).toFixed(1) + 'K';
  return value.toFixed(1);
};

// Get display text for selection
const getSelectionDisplayText = (selected, options) => {
  if (selected.length === 0) return 'None Selected';
  if (selected.length === options.length) return 'All Selected';
  if (selected.length === 1) return selected[0];
  return `${selected.length} Selected`;
};

// Searchable Single-Select Component
const SearchableSingleSelect = ({ options, selected, onSelect, placeholder, id }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  const filteredOptions = options.filter(opt =>
    opt.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const displayText = selected ? selected : placeholder;

  return (
    <div className="dropdown-container" id={id} ref={dropdownRef}>
      <div className="dropdown-header" onClick={() => setIsOpen(!isOpen)}>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={displayText}
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(true);
          }}
          className="dropdown-search-input"
        />
        <span className="dropdown-arrow">{isOpen ? '▲' : '▼'}</span>
      </div>
      {isOpen && (
        <div className="dropdown-menu">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((opt, index) => (
              <div
                key={index}
                className={`dropdown-item ${selected === opt ? 'selected' : ''}`}
                onClick={() => {
                  onSelect(opt);
                  setIsOpen(false);
                  setSearchTerm('');
                }}
              >
                <input
                  type="radio"
                  checked={selected === opt}
                  readOnly
                />
                <span>{opt}</span>
              </div>
            ))
          ) : (
            <div className="dropdown-item no-options">No options found</div>
          )}
        </div>
      )}
    </div>
  );
};

// Searchable Multi-Select Component
const SearchableMultiSelect = ({ options, selected, onToggle, onSelectAll, onDeselectAll, placeholder, id }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  const filteredOptions = options.filter(opt => opt.toLowerCase().includes(searchTerm.toLowerCase()));

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const displayText = getSelectionDisplayText(selected, options);

  return (
    <div className="dropdown-container" id={id} ref={dropdownRef}>
      <div className="dropdown-header" onClick={() => setIsOpen(!isOpen)}>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={displayText}
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(true);
          }}
          className="dropdown-search-input"
        />
        <span className="dropdown-arrow">{isOpen ? '▲' : '▼'}</span>
      </div>
      {isOpen && (
        <div className="dropdown-menu">
          <div className="dropdown-actions">
            <button className="dropdown-action-button" onClick={onSelectAll}>Select All</button>
            <button className="dropdown-action-button" onClick={onDeselectAll}>Deselect All</button>
          </div>
          {filteredOptions.length > 0 ? (
            filteredOptions.map((opt, index) => (
              <div
                key={index}
                className={`dropdown-item ${selected.includes(opt) ? 'selected' : ''}`}
                onClick={() => onToggle(opt)}
              >
                <input
                  type="checkbox"
                  checked={selected.includes(opt)}
                  readOnly
                />
                <span>{opt}</span>
              </div>
            ))
          ) : (
            <div className="dropdown-item no-options">No options found</div>
          )}
        </div>
      )}
    </div>
  );
};

// Clean up long variable names from the CSV for display
const cleanVariableName = (rawName) => {
  if (!rawName) return rawName;
  return rawName.replace(/^Potential Fiscal Space Created for\s*/i, '').trim();
};

// Clean up unit of measure labels
const cleanIndicatorName = (rawUnit) => {
  if (!rawUnit) return rawUnit;
  if (rawUnit.toLowerCase().includes('per capita')) {
    return 'Per Capita Fiscal Space (Current USD)';
  }
  return 'Total Fiscal Space (Current USD)';
};

// Main Component
const HealthDataVisualizer = () => {
  // State variables
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [dataStructure, setDataStructure] = useState({
    countries: [],
    indicators: [],
    variables: [],
    years: []
  });

  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedIndicator, setSelectedIndicator] = useState(null);
  const [selectedVariables, setSelectedVariables] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);

  const [chartType, setChartType] = useState('timeseries');
  const [showLegend, setShowLegend] = useState(true);
  const [showTargetLine, setShowTargetLine] = useState(false);
  const [targetLineValue, setTargetLineValue] = useState(0);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [showData, setShowData] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  const chartRef = useRef(null);
  const contentAreaRef = useRef(null);

  const colorPalette = [
    '#3498DB', '#2ECC71', '#F39C12', '#9B59B6',
    '#E74C3C', '#1ABC9C', '#E91E63', '#00BCD4',
  ];

  const variableOrder = [
    'Macroeconomic growth',
    'Budget reprioritization',
    'Taxes',
    'Loans'
  ];

  const getSortedVariables = (vars) => {
    return [...vars].sort((a, b) => {
      const aIdx = variableOrder.findIndex(v => a.toLowerCase().includes(v.toLowerCase()));
      const bIdx = variableOrder.findIndex(v => b.toLowerCase().includes(v.toLowerCase()));
      if (aIdx === -1 && bIdx === -1) return a.localeCompare(b);
      if (aIdx === -1) return 1;
      if (bIdx === -1) return -1;
      return aIdx - bIdx;
    });
  };

  const exportToPNG = async () => {
    const contentArea = contentAreaRef.current;
    if (!contentArea) return;

    try {
      const canvas = await html2canvas(contentArea, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true
      });

      const finalCanvas = document.createElement('canvas');
      const ctx = finalCanvas.getContext('2d');
      finalCanvas.width = canvas.width;
      finalCanvas.height = canvas.height;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);
      ctx.drawImage(canvas, 0, 0);

      const currentYear = new Date().getFullYear();
      ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
      ctx.font = 'italic 22px Arial, sans-serif';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'bottom';
      ctx.fillText(`\u00A9 Health Systems Innovation Lab, Harvard University, ${currentYear}`, 40, canvas.height - 30);

      const logo = new Image();
      logo.crossOrigin = 'anonymous';
      logo.onload = () => {
        const logoHeight = 50;
        const logoWidth = logoHeight * (logo.width / logo.height);
        ctx.drawImage(logo, finalCanvas.width - logoWidth - 40, canvas.height - logoHeight - 20, logoWidth, logoHeight);
        finalCanvas.toBlob(blob => {
          saveAs(blob, `fiscal_space_${(selectedCountry || 'chart').replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.png`);
        });
      };
      logo.onerror = () => {
        finalCanvas.toBlob(blob => {
          saveAs(blob, `fiscal_space_${(selectedCountry || 'chart').replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.png`);
        });
      };
      logo.src = '/logo.png';
    } catch (error) {
      console.error('Error exporting to PNG:', error);
      alert('Failed to export image. Please try again.');
    }
  };

  // Load CSV data with random initial selection
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const csvResponse = await fetch('/Current/Fiscal_Space_Feb2.csv');
        if (!csvResponse.ok) throw new Error(`HTTP error: ${csvResponse.status}`);
        const csvText = await csvResponse.text();

        Papa.parse(csvText, {
          header: true,
          dynamicTyping: false,
          skipEmptyLines: true,
          complete: results => {
            const cleanedData = results.data
              .filter(row => row.Country && row.Variable && row.Year && row.Value !== undefined)
              .map(row => {
                const rawVariable = (row.Variable || '').trim();
                const rawUnit = (row['Unit of Measure'] || '').trim();
                const cleanVar = cleanVariableName(rawVariable);
                const cleanInd = cleanIndicatorName(rawUnit);
                return {
                  Country: (row.Country || '').trim(),
                  ISO: (row['ISO 3'] || '').trim(),
                  Variable: cleanVar,
                  RawVariable: rawVariable,
                  Indicator: cleanInd,
                  RawIndicator: rawUnit,
                  Year: parseInt(row.Year),
                  Value: parseFloat(row.Value)
                };
              })
              .filter(row => !isNaN(row.Year) && !isNaN(row.Value));

            const rawVars = [...new Set(cleanedData.map(row => row.Variable))];
            const sortedVars = getSortedVariables(rawVars);
            const yearsArray = [...new Set(cleanedData.map(row => row.Year))].sort((a, b) => a - b);

            const structure = {
              countries: [...new Set(cleanedData.map(row => row.Country))].sort(),
              indicators: [...new Set(cleanedData.map(row => row.Indicator))].sort((a, b) => {
                if (a.toLowerCase().includes('per capita')) return -1;
                if (b.toLowerCase().includes('per capita')) return 1;
                return a.localeCompare(b);
              }),
              variables: sortedVars,
              years: yearsArray
            };

            // RANDOM INITIAL SELECTION
            const randomCountry = structure.countries.length > 0
              ? structure.countries[Math.floor(Math.random() * structure.countries.length)]
              : null;
            const randomIndicator = structure.indicators.length > 0
              ? structure.indicators[Math.floor(Math.random() * structure.indicators.length)]
              : null;
            const randomYear = yearsArray.length > 0
              ? yearsArray[Math.floor(Math.random() * yearsArray.length)]
              : null;

            // Set all state together
            setData(cleanedData);
            setDataStructure(structure);
            setSelectedCountry(randomCountry);
            setSelectedIndicator(randomIndicator);
            setSelectedVariables(sortedVars); // Select all variables
            setSelectedYear(randomYear);
            setLoading(false);
          },
          error: error => {
            console.error('Error parsing CSV:', error);
            setLoading(false);
          }
        });
      } catch (error) {
        console.error('Error loading data:', error);
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Filter data based on selections
  useEffect(() => {
    if (data.length === 0 || !selectedCountry || !selectedIndicator || selectedVariables.length === 0) {
      setFilteredData([]);
      return;
    }
    if (chartType === 'grouped' && !selectedYear) {
      setFilteredData([]);
      return;
    }

    const filtered = data.filter(row => {
      return row.Country === selectedCountry &&
        row.Indicator === selectedIndicator &&
        selectedVariables.includes(row.Variable) &&
        (chartType === 'timeseries' || row.Year === selectedYear);
    });
    setFilteredData(filtered);
  }, [data, selectedCountry, selectedIndicator, selectedVariables, selectedYear, chartType]);

  // Responsive chart resize
  const resizeChart = useCallback(() => {
    if (chartRef.current) {
      Plotly.Plots.resize(chartRef.current);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('resize', resizeChart);
    return () => window.removeEventListener('resize', resizeChart);
  }, [resizeChart]);

  // Resize chart when sidebar toggles
  useEffect(() => {
    const timer = setTimeout(resizeChart, 350);
    return () => clearTimeout(timer);
  }, [isSidebarOpen, resizeChart]);

  // Update chart
  useEffect(() => {
    if (!chartRef.current || filteredData.length === 0) {
      if (chartRef.current) Plotly.purge(chartRef.current);
      return;
    }
    if (chartType === 'timeseries') {
      renderTimeSeriesChart();
    } else {
      renderGroupedChart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredData, chartType, showLegend, showTargetLine, targetLineValue]);

  const renderTimeSeriesChart = () => {
    const traces = [];
    const variables = getSortedVariables([...new Set(filteredData.map(row => row.Variable))]);

    variables.forEach((variable, idx) => {
      const varData = filteredData.filter(row => row.Variable === variable).sort((a, b) => a.Year - b.Year);
      if (varData.length === 0) return;
      const color = colorPalette[idx % colorPalette.length];
      traces.push({
        x: varData.map(row => row.Year),
        y: varData.map(row => row.Value),
        mode: 'lines+markers',
        name: variable,
        line: { color, width: 2.5, shape: 'spline', smoothing: 0.8 },
        marker: { color, size: 6, line: { color: 'white', width: 1.5 } },
        type: 'scatter',
        hovertemplate: `<b>${variable}</b><br>Year: %{x}<br>Value: %{y:,.2f}<extra></extra>`
      });
    });

    if (showTargetLine && targetLineValue !== null) {
      const years = [...new Set(filteredData.map(row => row.Year))].sort((a, b) => a - b);
      traces.push({
        x: [Math.min(...years), Math.max(...years)],
        y: [targetLineValue, targetLineValue],
        mode: 'lines',
        name: `Target (${formatAbbreviated(targetLineValue)})`,
        line: { color: '#6B7280', width: 2, dash: 'dash' },
        type: 'scatter'
      });
    }

    const allYValues = filteredData.map(row => row.Value);
    if (showTargetLine) allYValues.push(targetLineValue);
    const yMin = Math.min(...allYValues);
    const yMax = Math.max(...allYValues);
    const yPadding = (yMax - yMin) * 0.1 || 1;

    const layout = {
      title: {
        text: `<b>${selectedCountry} — Potential Fiscal Space Created from Different Sources</b>`,
        font: { size: 24, color: '#374151' },
        x: 0.5
      },
      xaxis: {
        title: { text: 'Year', font: { size: 12, color: '#6B7280' } },
        showgrid: true,
        gridcolor: '#E5E7EB',
        tickmode: 'linear',
        dtick: 1,
        tickfont: { size: 11 }
      },
      yaxis: {
        title: {
          text: selectedIndicator?.toLowerCase().includes('per capita') ? 'USD per Capita' : 'USD',
          font: { size: 12, color: '#6B7280' }
        },
        showgrid: true,
        gridcolor: '#E5E7EB',
        tickfont: { size: 11 },
        tickformat: '',
        tickprefix: '',
        exponentformat: 'none',
        separatethousands: true,
        range: [Math.min(0, yMin - yPadding), yMax + yPadding],
        // Custom tick values for abbreviated formatting
        ...(yMax >= 1e6 ? {
          tickvals: (() => {
            const range = yMax - Math.min(0, yMin);
            const step = Math.pow(10, Math.floor(Math.log10(range))) / 2;
            const ticks = [];
            for (let v = 0; v <= yMax + yPadding; v += step) ticks.push(v);
            if (yMin < 0) for (let v = -step; v >= yMin - yPadding; v -= step) ticks.push(v);
            return ticks.sort((a, b) => a - b);
          })(),
          ticktext: (() => {
            const range = yMax - Math.min(0, yMin);
            const step = Math.pow(10, Math.floor(Math.log10(range))) / 2;
            const ticks = [];
            for (let v = 0; v <= yMax + yPadding; v += step) ticks.push(v);
            if (yMin < 0) for (let v = -step; v >= yMin - yPadding; v -= step) ticks.push(v);
            return ticks.sort((a, b) => a - b).map(v => formatAbbreviated(v));
          })()
        } : { tickformat: ',.0f' })
      },
      showlegend: showLegend,
      legend: {
        orientation: 'h',
        x: 0.5,
        xanchor: 'center',
        y: -0.18,
        yanchor: 'top',
        font: { size: 11 }
      },
      hovermode: 'x unified',
      plot_bgcolor: 'white',
      paper_bgcolor: 'white',
      margin: { l: 70, r: 20, t: 50, b: 80 },
      autosize: true
    };

    Plotly.newPlot(chartRef.current, traces, layout, {
      responsive: true,
      displaylogo: false,
      displayModeBar: false
    });
  };

  const renderGroupedChart = () => {
    const traces = [];
    const variables = getSortedVariables([...new Set(filteredData.map(row => row.Variable))]);
    const allYValues = [];

    variables.forEach((variable, idx) => {
      const varData = filteredData.filter(row => row.Variable === variable);
      if (varData.length === 0) return;
      const avgValue = varData.reduce((sum, row) => sum + row.Value, 0) / varData.length;
      allYValues.push(avgValue);
      traces.push({
        x: [variable],
        y: [avgValue],
        type: 'bar',
        marker: { color: colorPalette[idx % colorPalette.length] },
        name: variable,
        hovertemplate: `<b>${variable}</b><br>Value: %{y:,.2f}<extra></extra>`
      });
    });

    const yMax = allYValues.length > 0 ? Math.max(...allYValues) : 1;
    const yMin = allYValues.length > 0 ? Math.min(...allYValues) : 0;

    const layout = {
      title: {
        text: `<b>${selectedCountry} — Potential Fiscal Space Created from Different Sources (${selectedYear})</b>`,
        font: { size: 24, color: '#374151' },
        x: 0.5
      },
      xaxis: {
        title: { text: 'Fiscal Space Source', font: { size: 12, color: '#6B7280' } },
        tickangle: -20,
        automargin: true,
        tickfont: { size: 11 }
      },
      yaxis: {
        title: {
          text: selectedIndicator?.toLowerCase().includes('per capita') ? 'USD per Capita' : 'USD',
          font: { size: 12, color: '#6B7280' }
        },
        showgrid: true,
        gridcolor: '#E5E7EB',
        tickfont: { size: 11 },
        range: [Math.min(0, yMin - (yMax - yMin) * 0.1), yMax + (yMax - yMin) * 0.1],
        ...(yMax >= 1e6 ? {
          tickvals: (() => {
            const range = yMax - Math.min(0, yMin);
            const step = Math.pow(10, Math.floor(Math.log10(range))) / 2;
            const ticks = [];
            for (let v = 0; v <= yMax + (yMax - yMin) * 0.1; v += step) ticks.push(v);
            if (yMin < 0) for (let v = -step; v >= yMin - (yMax - yMin) * 0.1; v -= step) ticks.push(v);
            return ticks.sort((a, b) => a - b);
          })(),
          ticktext: (() => {
            const range = yMax - Math.min(0, yMin);
            const step = Math.pow(10, Math.floor(Math.log10(range))) / 2;
            const ticks = [];
            for (let v = 0; v <= yMax + (yMax - yMin) * 0.1; v += step) ticks.push(v);
            if (yMin < 0) for (let v = -step; v >= yMin - (yMax - yMin) * 0.1; v -= step) ticks.push(v);
            return ticks.sort((a, b) => a - b).map(v => formatAbbreviated(v));
          })()
        } : { tickformat: ',.0f' })
      },
      showlegend: showLegend,
      legend: {
        orientation: 'h',
        x: 0.5,
        xanchor: 'center',
        y: -0.3,
        yanchor: 'top',
        font: { size: 11 }
      },
      plot_bgcolor: 'white',
      paper_bgcolor: 'white',
      margin: { l: 70, r: 20, t: 50, b: 100 },
      autosize: true
    };

    Plotly.newPlot(chartRef.current, traces, layout, {
      responsive: true,
      displaylogo: false,
      displayModeBar: false
    });
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const toggleData = () => setShowData(!showData);

  const handleVariableToggle = (variable) => {
    setSelectedVariables(prev =>
      prev.includes(variable) ? prev.filter(v => v !== variable) : [...prev, variable]
    );
  };

  let tableData = [...filteredData];
  if (sortConfig.key && tableData.length > 0) {
    tableData.sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      if (typeof aVal === 'string') return sortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }

  if (loading) {
    return <div className="loading-container"><div className="loading-message">Loading Fiscal Space Data...</div></div>;
  }

  const hasAllSelections = selectedCountry && selectedIndicator && selectedVariables.length > 0 &&
    (chartType === 'timeseries' || selectedYear);

  return (
    <div className="explorer-container">
      {/* Sidebar Toggle Button - floating top-left */}
      <button
        className="sidebar-toggle-btn"
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        title={isSidebarOpen ? 'Collapse panel' : 'Expand panel'}
      >
        {isSidebarOpen ? '\u25C0' : '\u25B6'}
      </button>

      {isSidebarOpen && (
        <div className="sidebar open">
          <div className="sidebar-content">
            {/* Chart Type - Visible pill selector */}
            <div className="section">
              <h2 className="section-title">Chart Type</h2>
              <div className="chart-type-pills">
                <button
                  className={`chart-type-pill ${chartType === 'timeseries' ? 'active' : ''}`}
                  onClick={() => setChartType('timeseries')}
                >
                  Longitudinal
                </button>
                <button
                  className={`chart-type-pill ${chartType === 'grouped' ? 'active' : ''}`}
                  onClick={() => setChartType('grouped')}
                >
                  Cross-sectional
                </button>
              </div>
            </div>

            <div className="section">
              <h2 className="section-title">Country</h2>
              <SearchableSingleSelect options={dataStructure.countries} selected={selectedCountry} onSelect={setSelectedCountry} placeholder="Select Country" id="countries-select" />
            </div>

            <div className="section">
              <h2 className="section-title">Unit of Measure</h2>
              <SearchableSingleSelect options={dataStructure.indicators} selected={selectedIndicator} onSelect={setSelectedIndicator} placeholder="Select Unit" id="indicators-select" />
            </div>

            <div className="section">
              <h2 className="section-title">Fiscal Space Sources</h2>
              <SearchableMultiSelect options={dataStructure.variables} selected={selectedVariables} onToggle={handleVariableToggle}
                onSelectAll={() => setSelectedVariables(dataStructure.variables)} onDeselectAll={() => setSelectedVariables([])} placeholder="Select Sources" id="variables-select" />
            </div>

            {chartType === 'grouped' && (
              <div className="section">
                <h2 className="section-title">Year</h2>
                <SearchableSingleSelect
                  options={dataStructure.years.map(String)}
                  selected={selectedYear ? String(selectedYear) : null}
                  onSelect={(val) => setSelectedYear(parseInt(val))}
                  placeholder="Select Year"
                  id="year-select"
                />
              </div>
            )}

            <div className="section">
              <label className="checkbox-label">
                <input type="checkbox" checked={showLegend} onChange={(e) => setShowLegend(e.target.checked)} />
                <span>Show Legend</span>
              </label>
            </div>

            {chartType === 'timeseries' && (
              <div className="section">
                <label className="checkbox-label">
                  <input type="checkbox" checked={showTargetLine} onChange={(e) => setShowTargetLine(e.target.checked)} />
                  <span>Show Target Line</span>
                </label>
                {showTargetLine && (
                  <div style={{ marginTop: '8px', marginLeft: '24px' }}>
                    <label style={{ fontSize: '0.85rem', color: '#6B7280' }}>
                      Target Value:
                      <input type="number" value={targetLineValue} onChange={(e) => setTargetLineValue(parseFloat(e.target.value) || 0)}
                        style={{ width: '80px', marginLeft: '8px', padding: '4px 8px', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: '0.85rem' }} />
                    </label>
                  </div>
                )}
              </div>
            )}

            <div className="section">
              <button className="button button-secondary" onClick={toggleData} style={{ width: '100%' }}>
                {showData ? 'Hide Data Table' : 'Show Data Table'}
              </button>
            </div>

            {/* Metadata Section */}
            <div className="section metadata-section">
              <h2 className="section-title">Metadata</h2>
              <div className="metadata-content">
                <div className="metadata-row">
                  <span className="metadata-label">Dataset</span>
                  <span className="metadata-value">Potential Fiscal Space</span>
                </div>
                <div className="metadata-row">
                  <span className="metadata-label">Source</span>
                  <span className="metadata-value">HSIL</span>
                </div>
                {selectedCountry && (
                  <div className="metadata-row">
                    <span className="metadata-label">Country</span>
                    <span className="metadata-value">{selectedCountry}</span>
                  </div>
                )}
                {selectedIndicator && (
                  <div className="metadata-row">
                    <span className="metadata-label">Unit</span>
                    <span className="metadata-value">{selectedIndicator}</span>
                  </div>
                )}
                <div className="metadata-row">
                  <span className="metadata-label">Variables</span>
                  <span className="metadata-value">{selectedVariables.length} of {dataStructure.variables.length} selected</span>
                </div>
                <div className="metadata-row">
                  <span className="metadata-label">Year Range</span>
                  <span className="metadata-value">
                    {dataStructure.years.length > 0
                      ? `${dataStructure.years[0]} - ${dataStructure.years[dataStructure.years.length - 1]}`
                      : 'N/A'}
                  </span>
                </div>
                <div className="metadata-row">
                  <span className="metadata-label">Records</span>
                  <span className="metadata-value">{filteredData.length}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="main-content">
        {hasAllSelections && (
            <button onClick={exportToPNG} title="Download chart as PNG"
              style={{ position: 'fixed', top: 70, right: 24, zIndex: 100, display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 24px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '6px', cursor: 'pointer', fontSize: '18px', color: '#495057', fontWeight: '600', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              PNG
            </button>
          )}

        <div className="content-area" ref={contentAreaRef}>
          {!hasAllSelections ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#6B7280', fontSize: '1.1rem' }}>
              <p style={{ marginBottom: '1rem' }}>Please select all required options to display the chart:</p>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                <li style={{ color: selectedCountry ? '#10B981' : '#EF4444' }}>{selectedCountry ? '\u2713' : '\u25CB'} Country</li>
                <li style={{ color: selectedIndicator ? '#10B981' : '#EF4444' }}>{selectedIndicator ? '\u2713' : '\u25CB'} Unit of Measure</li>
                <li style={{ color: selectedVariables.length > 0 ? '#10B981' : '#EF4444' }}>{selectedVariables.length > 0 ? '\u2713' : '\u25CB'} Fiscal Space Sources</li>
                {chartType === 'grouped' && <li style={{ color: selectedYear ? '#10B981' : '#EF4444' }}>{selectedYear ? '\u2713' : '\u25CB'} Year</li>}
              </ul>
            </div>
          ) : (
            <div ref={chartRef} style={{ width: '100%', height: '100%' }} />
          )}
        </div>
      </div>

      {showData && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.9)', zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }} onClick={toggleData}>
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '2rem', maxWidth: '90vw', maxHeight: '80vh', width: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
              <div>
                <h3 style={{ margin: '0 0 4px 0', fontSize: '1.25rem', fontWeight: '600', color: '#111827' }}>
                  Potential Fiscal Space — {selectedCountry}
                </h3>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#6B7280' }}>
                  {selectedIndicator} &middot; {filteredData.length} records
                </p>
              </div>
              <button onClick={toggleData} style={{ backgroundColor: 'transparent', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#6B7280', alignSelf: 'flex-start' }}>&times;</button>
            </div>
            <div style={{ flex: 1, overflow: 'auto', border: '1px solid #E5E7EB', borderRadius: '4px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead style={{ position: 'sticky', top: 0, backgroundColor: '#F3F4F6' }}>
                  <tr>
                    <th style={{ padding: '12px', border: '1px solid #D1D5DB', textAlign: 'left', cursor: 'pointer' }} onClick={() => handleSort('Country')}>Country {sortConfig.key === 'Country' ? (sortConfig.direction === 'asc' ? '\u25B2' : '\u25BC') : ''}</th>
                    <th style={{ padding: '12px', border: '1px solid #D1D5DB', textAlign: 'left', cursor: 'pointer' }} onClick={() => handleSort('Variable')}>Fiscal Space Source {sortConfig.key === 'Variable' ? (sortConfig.direction === 'asc' ? '\u25B2' : '\u25BC') : ''}</th>
                    <th style={{ padding: '12px', border: '1px solid #D1D5DB', textAlign: 'left', cursor: 'pointer' }} onClick={() => handleSort('Indicator')}>Unit {sortConfig.key === 'Indicator' ? (sortConfig.direction === 'asc' ? '\u25B2' : '\u25BC') : ''}</th>
                    <th style={{ padding: '12px', border: '1px solid #D1D5DB', textAlign: 'left', cursor: 'pointer' }} onClick={() => handleSort('Year')}>Year {sortConfig.key === 'Year' ? (sortConfig.direction === 'asc' ? '\u25B2' : '\u25BC') : ''}</th>
                    <th style={{ padding: '12px', border: '1px solid #D1D5DB', textAlign: 'right', cursor: 'pointer' }} onClick={() => handleSort('Value')}>Value {sortConfig.key === 'Value' ? (sortConfig.direction === 'asc' ? '\u25B2' : '\u25BC') : ''}</th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((row, index) => (
                    <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F9FAFB' }}>
                      <td style={{ padding: '8px 12px', border: '1px solid #E5E7EB' }}>{row.Country}</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #E5E7EB' }}>{row.Variable}</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #E5E7EB' }}>{row.Indicator}</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #E5E7EB' }}>{row.Year}</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #E5E7EB', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{row.Value?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      <Tooltip id="info-tooltip" place="right" />
    </div>
  );
};

export default HealthDataVisualizer;
